import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusState } from '../../Hooks/useBizQuery';
import './biz-button.scss';
import { Link, useNavigate } from 'react-router-dom';
import { getIconClassByType, TBizIcon } from './BizIcon';
import { useLegacy } from '../../Hooks/admin/useLegacy';
import { BizControlSize } from '../../Types/controlSize';

export type BtnIcon =
	| 'far fa-save'
	| 'far fa-check'
	| 'fal fa-exclamation-triangle'
	| 'fa-solid fa-plus'
	| 'fas fa-reply'
	| 'fad fa-spinner-third fa-spin'
	| 'far fa-upload'
	| 'far fa-eye'
	| 'fa-solid fa-arrow-left'
	| 'fa-solid fa-arrow-right'
	| 'far fa-times'
	| 'fa-solid fa-comment'
	| 'far fa-undo';

export type TBizButtonSize = BizControlSize;

type BizButtonProps = {
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
	text: string;
	icon?: BtnIcon;
	isLead?: boolean;
	style?: React.CSSProperties;
	state?: StatusState;
	legacy?: boolean;
	className?: string;
	disabled?: boolean;
	onlyShowIconOnLoading?: boolean;
	size?: TBizButtonSize;
	type?: 'button' | 'submit' | 'reset';
};

export function BizButton({
	onClick,
	text,
	icon: pIcon,
	isLead,
	style,
	state,
	legacy: pLegacy,
	className,
	disabled,
	onlyShowIconOnLoading,
	size = 'MEDIUM',
	type,
}: BizButtonProps) {
	const legacy = useLegacy(pLegacy);
	const [internalState, setInternalState] = useState<StatusState>(state ?? 'INITIAL');

	useEffect(() => {
		if (state) {
			setInternalState(state);
		}
	}, [state]);

	useEffect(() => {
		if (internalState === 'SUCCESS' || internalState === 'ERROR') {
			const timer = setTimeout(() => {
				setInternalState('INITIAL');
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [internalState]);

	const baseClasses = `biz-button-base biz-button--size-${size?.toLowerCase()}`;
	let classes = `biz-button ${baseClasses}`;
	if (legacy) {
		classes = `admin-btn ${baseClasses}`;
	}

	if (isLead) {
		classes += ' btn-lead';
	}

	if (className) {
		classes += ` ${className}`;
	}

	let icon = pIcon;

	if (internalState === 'LOADING') {
		icon = 'fad fa-spinner-third fa-spin';
	} else if (internalState === 'SUCCESS') {
		icon = 'far fa-check';
	} else if (internalState === 'ERROR') {
		icon = 'fal fa-exclamation-triangle';
	}

	if (legacy) {
		if (disabled) {
			classes += ' disabled';
		}

		// An explicit type keeps legacy styling with native form and keyboard behavior.
		if (type) {
			return (
				<button
					type={type}
					className={classes}
					onClick={state === 'LOADING' || disabled ? undefined : onClick}
					style={style}
					disabled={state === 'LOADING' || disabled}
				>
					{icon ? <i className={icon} /> : null} {text}
				</button>
			);
		}

		return (
			<span
				className={classes}
				onClick={state === 'LOADING' ? undefined : onClick}
				style={style}
				aria-disabled={disabled}
				role='button'
				tabIndex={0}
			>
				{icon ? <i className={icon} /> : null} {text}
			</span>
		);
	}

	if (internalState === 'LOADING') {
		classes += ' loading';
	} else if (internalState === 'SUCCESS' && !onlyShowIconOnLoading) {
		classes += ' success';
	} else if (internalState === 'ERROR') {
		classes += ' error';
	}

	const isDisabled = state === 'LOADING' || disabled;

	if (onlyShowIconOnLoading && internalState !== 'LOADING') {
		icon = undefined;
	}

	const isStateIcon = internalState === 'LOADING' || internalState === 'SUCCESS' || internalState === 'ERROR';

	return (
		<button type={type} className={classes} style={style} onClick={isDisabled ? undefined : onClick} disabled={isDisabled}>
			{icon !== undefined && (
				<div className={`btn-icon-wrapper${isStateIcon ? ' state-info' : ''}`}>
					<i className={icon} />
				</div>
			)}
			<span className='label'>{text}</span>
		</button>
	);
}

type BizLinkButtonProps = {
	text: string;
	link?: string;
	icon?: TBizIcon;
	isLead?: boolean;
	isBackBtn?: boolean;
	legacy?: boolean;
};

export function BizLinkBackButton({ text, icon, isLead, legacy }: Partial<Omit<BizLinkButtonProps, 'isBackBtn'>>) {
	const { t } = useTranslation();
	return (
		<BizLinkButton text={text || t('GLOBAL.BACK')} icon={icon || 'REPLY'} isLead={isLead} isBackBtn={true} legacy={legacy} />
	);
}

export function BizLinkButton({ link, text, icon: pIcon, isLead, isBackBtn, legacy }: BizLinkButtonProps) {
	const navigate = useNavigate();

	let classes = 'biz-button';
	if (legacy) {
		classes = 'admin-btn';
	}

	if (isLead) {
		classes += ' btn-lead';
	}

	const icon = pIcon ? getIconClassByType(pIcon) : undefined;

	if (isBackBtn) {
		return (
			<span className={classes} onClick={() => navigate(-1)}>
				{icon ? <i className={icon} /> : null} {text}
			</span>
		);
	}

	if (!link) {
		return null;
	}

	return (
		<Link to={link}>
			<span className={classes}>
				{icon ? <i className={icon} /> : null} {text}
			</span>
		</Link>
	);
}
